import React, { forwardRef } from 'react';
import { Pressable, PressableProps, View } from 'react-native';

import { useHover } from '@hooks/useHover';

interface HoverableProps extends Omit<PressableProps, 'children'> {
  children: (isHovered: boolean) => React.ReactNode;
  style?: any;
  accessibilityLabel?: string;
}

export const SimpleHoverable = forwardRef<View, HoverableProps>(
  (
    {
      children,
      style,
      accessibilityLabel,
      onHoverIn,
      onHoverOut,
      onPress,
      ...pressableProps
    },
    ref
  ) => {
    // Canonical hover behaviour (TA-9005). Keyboard activation stays here since
    // it is a Pressable-specific concern, not part of hover state.
    const { isHovered, hoverProps } = useHover({
      onHoverIn: onHoverIn as () => void,
      onHoverOut: onHoverOut as () => void,
    });

    const handleKeyDown = (e: any) => {
      const key = e.key ?? e.nativeEvent?.key;
      if (key === 'Enter' || key === ' ') {
        e.preventDefault?.();
        onPress?.(e);
      }
    };

    return (
      <Pressable
        ref={ref}
        {...pressableProps}
        accessibilityLabel={accessibilityLabel}
        // @ts-expect-error - onKeyDown is supported on web Pressable
        onKeyDown={handleKeyDown}
        onHoverIn={hoverProps.onHoverIn}
        onHoverOut={hoverProps.onHoverOut}
        onPress={onPress}
        style={style}>
        {children(isHovered)}
      </Pressable>
    );
  }
);
